import { useCallback, useState } from 'react';
import { Linking } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';

import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast'
import type { ScreenRefreshResult } from '../../shared/ui/screenRefreshFeedback';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { pullCustomerDataWhenNeeded } from '../../sync/core/pullCustomerWhenNeeded';
import { pullCustomerDataFromSupabaseApplication } from '../../sync/core/pullCustomersUseCase';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { clearWebCustomerSnapshotCache, loadWebCustomerSnapshot } from '../infra/webCustomerSnapshotCache'
import { getCustomerMovementHistory } from '../infra/customerRepository'
import { isCustomerOverdue } from '../application/customerDunning'
import type { CustomerAccountRow } from '../domain/customerTypes'
import { buildCustomerListCsv } from '../application/customerListCsv'
import { countCustomerAccounts, fetchCustomerAccounts } from '../infra/customerRepository'

import { useCustomerKpis } from '../application/useCustomerKpis'
import { useCustomerOverlayStore } from '../store/useCustomerOverlayStore';
import {
  useCustomerListFlow,
  type CustomerSortBy,
  type CustomerStatusFilter,
  type CustomerUtilizationFilter,
  type CustomerDatePreset,
} from './hooks/useCustomerListFlow';
import { MOVEMENT_PREVIEW_SIZE } from '../domain/customerConstants';
import { formatMoney } from '../../shared/format/public-api';

/** Facade de estado y listado del módulo Clientes. */
export function useCustomersScreen() {
  const route = useRoute();
  const { width: layoutWidth, layoutWide: isWideCustomerLayout } = useTabWorkspaceLayout();

  const { canEdit } = useBranchPermission('Customers');
  const canViewDebt = useModulePermission('Customers', 'view_debt');
  const canEditContact = useModulePermission('Customers', 'edit_contact');
  const canSetCreditLimit = useModulePermission('Customers', 'set_credit_limit');
  const canEditCustomer = canEdit || canEditContact;
  // No ensanchar: set_credit_limit / edit_contact no otorgan cobro ni ajuste.
  const canCollect = useModulePermission('Customers', 'collect') || canEdit;
  const canAdjustDebt = useModulePermission('Customers', 'adjust_debt') || canEdit;
  const canManageTasks = useModulePermission('Customers', 'manage_tasks') || canEditCustomer;
  const { entitlements: planEnt } = usePlanEntitlements();
  const portalBaseUrl =
    process.env.EXPO_PUBLIC_CUSTOMER_PORTAL_URL?.replace(/\/$/, '') ?? 'https://www.vuelto.ar';
  const paymentLinkAllowed = !planEnt || planEnt.fiado_payment_link !== false;

  const [statusFilter, setStatusFilter] = useState<CustomerStatusFilter>('all');
  const [sortBy, setSortBy] = useState<CustomerSortBy>('updated_desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [utilizationFilter, setUtilizationFilter] = useState<CustomerUtilizationFilter>('all');
  const [datePreset, setDatePreset] = useState<CustomerDatePreset>('all');

  const {
    movementModal,
    selectedCustomer,
    setMovements,
    setMovementLoading,
    setMovementsOffset,
    setMovementsHasMore,
    setMovementsLoadingMore,
  } = useCustomerOverlayStore();

  const listFlow = useCustomerListFlow({ searchQuery, statusFilter, sortBy, utilizationFilter, datePreset });
  const {
    customers,
    customerPageIndex,
    customerPageSize,
    setCustomerPageSize,
    customerHasMore,
    customerLoading,
    customerError,
    customerResultTotalCount,
    loadCustomers,
    loadCustomerNextPage,
    loadCustomerPreviousPage,
    refreshCustomerTotalCount,
    customerListRef,
    customerWideScrollRef,
  } = listFlow;

  const currentBranchId = useSessionStore((s) => s.currentBranchId);

  const customerKpis = useCustomerKpis(currentBranchId ?? null);
  const { daySummary, kpiSummary, refresh: refreshCustomerKpis } = customerKpis;

  const loadFn = useCallback(async (opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
    if (opts?.force) {
      clearWebCustomerSnapshotCache();
                await loadWebCustomerSnapshot(true);
      await refreshCustomerTotalCount();
      await loadCustomers();
      void refreshCustomerKpis();

      if (movementModal && selectedCustomer) {
        setMovementLoading(true);
        setMovementsOffset(0);
        setMovementsHasMore(false);
        setMovementsLoadingMore(false);
        try {
          const rows = await getCustomerMovementHistory(
            selectedCustomer.id,
            MOVEMENT_PREVIEW_SIZE,
            0,
            {}
          );
          setMovements(rows);
          setMovementsOffset(rows.length);
          setMovementsHasMore(rows.length === MOVEMENT_PREVIEW_SIZE);
        } finally {
          setMovementLoading(false);
        }
      }
    } else {
      await pullCustomerDataWhenNeeded();
      await refreshCustomerTotalCount();
      await loadCustomers();
    }
  }, [loadCustomers, refreshCustomerTotalCount, movementModal, selectedCustomer, currentBranchId, refreshCustomerKpis]);

  const { refreshing, onRefresh: handleRefresh } = useScreenLoad(
    route.name,
    loadFn
  );

  const [reminderQueue, setReminderQueue] = useState<CustomerAccountRow[] | null>(null);

  const bulkRemind = useCallback(async () => {
    if (!canEditCustomer && !canCollect) {
      showError('Modo solo lectura', 'Tu perfil no puede mandar recordatorios.');
      return;
    }
    // La lista es paginada: los vencidos pueden no estar en la página visible.
    // Traer el set completo con el filtro 'overdue' (misma regla que el KPI).
    const allOverdue = await fetchCustomerAccounts({
      query: { statusFilter: 'overdue' },
    });
    const overdue = allOverdue.filter(isCustomerOverdue);
    if (overdue.length === 0) {
      showInfo('Recordar vencidos', 'No hay clientes vencidos.');
      return;
    }
    // La cola abre un chat por gesto del usuario — nada de popups masivos.
    setReminderQueue(overdue);
  }, [canEditCustomer, canCollect]);

  const configurePortal = useCallback(() => {
    if (portalBaseUrl) {
      Linking.openURL(portalBaseUrl).catch(() => {});
    }
  }, [portalBaseUrl]);

  const exportList = useCallback(async () => {
    const totalCount = await countCustomerAccounts({
      search: searchQuery,
      statusFilter,
      sortBy,
      utilizationFilter,
      datePreset,
    });
    if (totalCount === 0) {
      showInfo('Exportar', 'No hay clientes para exportar con estos filtros.');
      return;
    }
    try {
      const allRows = await fetchCustomerAccounts({
        limit: totalCount,
        offset: 0,
        query: { search: searchQuery, statusFilter, sortBy, utilizationFilter, datePreset },
      });
      const csv = buildCustomerListCsv({
        rows: allRows,
        formatMoney: (n: number) => formatMoney(n),
      });
      const outcome = await shareOrDownloadCsv(csv, 'clientes.csv');
      if (outcome === 'ok') {
        showSuccess('Exportado', `Se descargaron ${allRows.length} clientes.`);
      }
    } catch (e) {
      if (e instanceof Error && /cancel/i.test(e.message)) return;
      showError('Exportar', 'No se pudo generar el archivo. Reintentá.');
    }
  }, [searchQuery, statusFilter, sortBy, utilizationFilter, datePreset]);

  return {
    layoutWidth,
    isWideCustomerLayout,
    daySummary,
    daySummaryLoading: customerKpis.loading,
    kpiSummary,
    kpiSummaryLoading: customerKpis.loading,
    canEdit,
    canEditCustomer,
    canViewDebt,
    canCollect,
    canAdjustDebt,
    canSetCreditLimit,
    canManageTasks,
    portalBaseUrl,
    paymentLinkAllowed,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    utilizationFilter,
    setUtilizationFilter,
    datePreset,
    setDatePreset,
    refreshing,
    handleRefresh,
    formatMoney: (n: number) => formatMoney(n),
    customers,
    customerPageIndex,
    customerPageSize,
    setCustomerPageSize,
    customerHasMore,
    customerLoading,
    customerError,
    customerResultTotalCount,
    loadCustomers,
    loadCustomerNextPage,
    loadCustomerPreviousPage,
    refreshCustomerTotalCount,
    customerListRef,
    customerWideScrollRef,
    bulkRemind,
    reminderQueue,
    closeReminderQueue: () => setReminderQueue(null),
    configurePortal,
    exportList,
  };
}
